import * as React from 'react';
import { BarChart } from '@mui/x-charts/BarChart';

export default function MonteCarloHistogram({
  dcfMC,                // expects { samples?: number[], histogram: { bins, binSize }, n, ... }
  currentPrice = null,  // optional
  height = 240,
  width = 400,
  trimPct = 0.025,      // trim bottom/top 2.5%
  numBins = 40,         // tighter ranges = more bins
  minCountThreshold = 5 // drop tiny bins after rebinning
}) {
  // --- helpers ---
  const makeCleanBins = (values, trimPct, numBins) => {
    const sorted = [...values].filter(Number.isFinite).sort((a, b) => a - b);
    if (!sorted.length) return [];

    const lo = sorted[Math.floor(sorted.length * trimPct)];
    const hi = sorted[Math.ceil(sorted.length * (1 - trimPct)) - 1];

    const min = lo;
    const max = hi;
    const binSize = (max - min) / numBins || 1;
    const bins = Array.from({ length: numBins }, (_, i) => ({
      x0: min + i * binSize,
      x1: min + (i + 1) * binSize,
      count: 0,
    }));

    for (const v of sorted) {
      if (v < min || v > max) continue;
      const idx = Math.min(numBins - 1, Math.floor((v - min) / binSize));
      bins[idx].count += 1;
    }
    return bins;
  };

  // If we have raw samples, use them for tighter, trimmed bins; else fall back to given bins.
  let bins = [];
  if (Array.isArray(dcfMC?.samples) && dcfMC.samples.length > 0) {
    bins = makeCleanBins(dcfMC.samples, trimPct, numBins);
  } else {
    // fallback: use provided bins, then drop extremes & small counts
    const raw = dcfMC?.histogram?.bins ?? [];
    const nonZero = raw.filter(b => b.count > 0);
    if (!nonZero.length) bins = [];
    else {
      // trim by counts on tails
      const total = nonZero.reduce((a, b) => a + b.count, 0);
      let cum = 0;
      const leftCutIdx = nonZero.findIndex(b => (cum += b.count) / total >= trimPct);
      cum = 0;
      let rightCutIdx = nonZero.length - 1;
      for (let i = nonZero.length - 1; i >= 0; i--) {
        cum += nonZero[i].count;
        if (cum / total >= trimPct) { rightCutIdx = i; break; }
      }
      bins = nonZero.slice(leftCutIdx, rightCutIdx + 1);
    }
  }

  // Filter tiny-count bins after (re)binning
  const tightBins = bins.filter(b => b.count > minCountThreshold);

  // Build dataset for vertical bars (x = range, y = count)
  const dataset = tightBins.map((b, i) => {
  const midVal = ((b.x0 + b.x1) / 2).toFixed(2);
  const inBin =
    currentPrice != null &&
    ((i < tightBins.length - 1 && currentPrice >= b.x0 && currentPrice < b.x1) ||
     (i === tightBins.length - 1 && currentPrice >= b.x0 && currentPrice <= b.x1));
  return {
    range: midVal, // midpoint instead of full range
    count: inBin ? 0 : b.count,
    highlightCount: inBin ? b.count : 0,
  };
});


  return (
    <BarChart
    tooltip={{
    trigger: 'none',
    render: () => null, // 👈 Completely disable tooltip rendering
  }}
 slots={{ tooltip: () => null }}


  borderRadius={10}
      dataset={dataset}
      height={height}
      width={width}
      margin={{ top: 10, right: 16, bottom: 30, left: 40 }}
      // Vertical chart: x = categories (price ranges), y = numeric counts
      xAxis={[{ 
        
        
      disableTicks: true, // 👈 removes tick lines
      disableLine: true,


        dataKey: 'range', scaleType: 'band', 
      ickLabelStyle: { fontSize: 8, angle: -45, textAnchor: 'end' },
    tickInterval: (value, index) => index % 7 === 0, // 👈 show 1 in every 4 ticks
      
      }]}
      yAxis={[{ 
      disableTicks: true, // 👈 removes tick lines
      disableLine: true,
        tickLabelStyle: { display: 'none' }
        }]}
      series={[
        { dataKey: 'count', stack: 's', layout: 'vertical',  color: '#5830b6'  },
        { dataKey: 'highlightCount', stack: 's', layout: 'vertical', color: '#ffcc00ff' },
      ]}
      slotProps={{
        legend: { 
          direction: 'horizontal',
          position: { vertical: 'bottom', horizontal: 'center' },
        },
        tooltip: {
          formatter: (value, seriesKey) => [
            `${value} runs`,
            seriesKey === 'highlightCount' ? 'Current price bin' : 'Simulations',
          ],
          labelFormatter: (label) => `Price range: ${label}`,
        },
      }}
    />
  );
}
